/**
 * Approvals — approver inbox (all content types) + review screen.
 * Verbatim port of the canonical type-aware ApprovalsInbox + ApproverReviewScreen.
 * Each submission routes to the approvers on its process; the review screen
 * shows a per-type summary, the status timeline, feedback history, and the
 * approve / reject / feedback decision panel with attachments.
 */
import { useEffect, useRef, useState } from "react";
import { uploadToSignedUrl } from "../../lib/api";
const AVATAR_GLYPH_RATIO = 0.42; // ui-config — initials glyph scales with avatar dot (presentational ratio, not data)
import { ChipToggleGroup, Btn, Card, Checkbox, Icon, Input, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { can, canApproveType, getUser } from "../../shell/access";
import { useNav } from "../../shell/router";
import { StatTile, FilterBar, EmptyState, ExplainerFooter, FilterDropdown, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar, useRowSelection, BulkActionBar, tabExport } from "../../shell/list-composites";
import {
  APPROVAL_TYPES, APPROVAL_TYPE, useApprovals, ApprovalStatusPill, ApprovalTimeline,
  apInput, apTh as thStyle, apTd as tdStyle, APPROVALS_COMING_SOON, type ApprovalTypeDef,
} from "./approvals-store";
import { useT, t as i18nT } from "../../lib/i18n";
import { toast } from "../../shell/toast";
import { approvalsCreateAttachmentUpload } from "@v0y4ge/api-client";

const InitialsDot = ({ initials, color, size = 20 }: { initials: string; color: string; size?: number }) => (
  <span style={{ width: size, height: size, borderRadius: 999, background: color, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: size * AVATAR_GLYPH_RATIO, fontWeight: 700, flexShrink: 0 }}>{initials.slice(0, 2).toUpperCase()}</span>
);
const typeBadge = (tm: ApprovalTypeDef) => {
  const IconMap = Icon as unknown as Record<string, (p?: { size?: number }) => React.ReactNode>;
  const I = IconMap[tm.icon] || IconMap.check;
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px 2px 7px", borderRadius: 999, background: T.n6, border: `1px solid ${T.border}`, fontSize: 11.5, fontWeight: 600, color: T.n2, whiteSpace: "nowrap" }}>{I({ size: 11 })} {tm.label}</span>;
};

/* Approvals are Coming soon (CC 0224): the queue and the review screen show this
   instead, and read nothing. */
function ApprovalsQueueComingSoon() {
  return (
    <Screen rail="approvals">
      <PageHeader
        title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>Approvals <span style={{ padding: "2px 8px", borderRadius: 999, background: T.p3, color: T.p1, fontSize: 12, fontWeight: 600 }}>Coming soon</span></span>}
        subtitle="Approval workflows are coming soon. Content goes live without sign-off for now."
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px" }} data-testid="approvals-queue-coming-soon">
        <EmptyState variant="card" icon={<Icon.check size={20} />} title="Nothing to review" body="When approvals are available, items waiting for sign-off will appear here." />
      </div>
    </Screen>
  );
}
export default function ApprovalsInbox() {
  return APPROVALS_COMING_SOON ? <ApprovalsQueueComingSoon /> : <ApprovalsInboxLive />;
}

/* ═══ Approver inbox (role-focused list, all content types) ══════════════ */
function ApprovalsInboxLive() {
  const t = useT();
  const nav = useNav();
  const store = useApprovals();
  const [view, setView] = useViewMode("approvals-inbox:view");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [procFilter, setProcFilter] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const procName = (id: string) => (store.getProcess(id) || { name: "—" }).name || "—";
  const canEdit = can("events.write");

  // Decorate each submission with its resolved item + type meta for display.
  const rows = store.all().map((sub) => {
    const tm = APPROVAL_TYPE(sub.type);
    const it = tm.getItem(sub.itemId);
    return { ...sub, tm, title: (it && tm.title(it)) || sub.itemId, hue: (it && (it as any).hue) || T.p1, process: procName(sub.processId) };
  });
  const counts: Record<string, number> = {
    all: rows.length,
    submitted: rows.filter((r) => r.status === "submitted").length,
    "under-review": rows.filter((r) => r.status === "under-review").length,
    feedback: rows.filter((r) => r.status === "feedback").length,
    approved: rows.filter((r) => r.status === "approved").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
  };
  const procs = [...new Set(rows.map((r) => r.process))];
  const typesPresent = [...new Set(rows.map((r) => r.type))];
  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (typeFilter !== "all" && r.type !== typeFilter) return false;
    if (procFilter && r.process !== procFilter) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      if (!`${r.title} ${r.submittedBy} ${r.process} ${r.tm.label}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sel = useRowSelection(filtered.map((r) => r.itemId));
  const anyFilter = statusFilter !== "all" || typeFilter !== "all" || procFilter || query.trim();

  // Map an approval submission's content type → the audited object type, so the
  // ⋯ menu can open that item's change history (admin-only).
  const AUDIT_TYPE: Record<string, import("../../lib/audit").AuditObjectType> = { events: "event", surveys: "survey", questions: "question", "question-groups": "questionGroup", promos: "promo", services: "service" };

  // The ONE standard action cluster — View opens the item, Review decides.
  const subActions = (r: typeof rows[number]) => ({
    onView: () => r.tm.open(nav, r.itemId),
    viewLabel: r.type === "events" ? "View draft" : "View",
    onEdit: canEdit ? () => r.tm.open(nav, r.itemId) : undefined,
    editLabel: "Open",
    audit: AUDIT_TYPE[r.type] ? { type: AUDIT_TYPE[r.type], id: r.itemId, name: r.title } : undefined,
    moreHeader: r.title,
    moreItems: [
      { icon: <Icon.check size={14} />, label: t("approvals.inbox.reviewSubmission", "Review submission"), sub: "Approve, request changes or reject", onClick: () => nav.go("approvals.review", { type: r.type, id: r.itemId }) },
      { icon: <Icon.download size={14} />, label: t("common.actions.export", "Export"), onClick: () => tabExport("approvals", [r] as unknown as Record<string, unknown>[]) },
    ],
  });

  const STAT_BLOCKS = [
    { id: "submitted",    label: t("approvals.inbox.submitted", "Submitted"),    icon: <Icon.send size={14} />,  accent: T.p1 },
    { id: "under-review", label: t("approvals.inbox.underReview", "Under review"), icon: <Icon.eye size={14} />,   accent: T.tDarkBlue },
    { id: "rejected",     label: t("approvals.inbox.rejected", "Rejected"),     icon: <Icon.x size={14} />,     accent: T.red1 },
    { id: "approved",     label: t("approvals.inbox.approved", "Approved"),     icon: <Icon.check size={14} />, accent: T.green1 },
  ];

  return (
    <Screen rail="approvals">
      <PageHeader
        title={t("approvals.inbox.approvals", "Approvals")}
        subtitle={t("approvals.inbox.filteredLengthOfRowsLengthShownAnyfilterFiltersActive.icu", "{shown} of {total} shown{filters, select, active { · filters active} other {}}", { shown: filtered.length, total: rows.length, filters: anyFilter ? "active" : "none" })}
        breadcrumb={null}
        actions={<Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => tabExport("approvals", filtered as unknown as Record<string, unknown>[])}>{t("common.actions.export", "Export")}</Btn>}
      />

      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {STAT_BLOCKS.map((b) => (
          <StatTile key={b.id} label={b.label} value={counts[b.id]} icon={b.icon} accent={b.accent}
            onClick={() => setStatusFilter(statusFilter === b.id ? "all" : b.id)} />
        ))}
      </div>

      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("approvals.inbox.searchItemSubmitterType", "Search item, submitter, type…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 260 }} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <ChipToggleGroup
          ariaLabel={t("approvals.inbox.ariaLabel", "Status filter")}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as string)}
          options={[
            { value: "all", label: i18nT("approvals.inbox.allCountsAll.icu", "All · {value0}", { value0: counts.all }) },
            { value: "submitted", label: i18nT("approvals.inbox.submittedCountsSubmitted.icu", "Submitted · {value0}", { value0: counts.submitted }) },
            { value: "under-review", label: i18nT("approvals.inbox.underReviewCountsUnderReview.icu", "Under review · {value0}", { value0: counts["under-review"] }) },
            { value: "feedback", label: i18nT("approvals.inbox.feedbackCountsFeedback.icu", "Feedback · {value0}", { value0: counts.feedback }) },
            { value: "approved", label: i18nT("approvals.inbox.approvedCountsApproved.icu", "Approved · {value0}", { value0: counts.approved }) },
            { value: "rejected", label: i18nT("approvals.inbox.rejectedCountsRejected.icu", "Rejected · {value0}", { value0: counts.rejected }) },
          ]}
        />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <FilterDropdown label={typeFilter === "all" ? t("approvals.approvalsInbox.anyType", "Any type") : APPROVAL_TYPE(typeFilter).label} active={typeFilter !== "all"} options={typesPresent.map((t) => APPROVAL_TYPE(t).label)} onPick={(lbl) => setTypeFilter((APPROVAL_TYPES.find((t) => t.label === lbl) || { id: "all" }).id)} onClear={() => setTypeFilter("all")} />
        <FilterDropdown label={procFilter || t("approvals.approvalsInbox.anyProcess", "Any process")} active={!!procFilter} options={procs} onPick={setProcFilter} onClear={() => setProcFilter(null)} />
        {anyFilter ? <Btn size="sm" variant="link" onClick={() => { setStatusFilter("all"); setTypeFilter("all"); setProcFilter(null); setQuery(""); }}>{t("common.actions.clearAll", "Clear all")}</Btn> : null}
        <span style={{ marginLeft: "auto" }} />
        <ViewToggle mode={view} onChange={setView} />
      </FilterBar>

      <BulkActionBar count={sel.selected.size} noun="submission" onClear={sel.clear} actions={[
        { label: t("common.actions.export", "Export"), icon: <Icon.download size={12} />, onClick: () => { tabExport("approvals", filtered.filter((x) => sel.selected.has(x.itemId)) as unknown as Record<string, unknown>[]); sel.clear(); } },
      ]} />

      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {filtered.length === 0 ? (
          <EmptyState variant="card" icon={<Icon.check size={20} />}
            title={anyFilter ? t("approvals.approvalsInbox.noSubmissionsMatchTheseFilters", "No submissions match these filters") : t("approvals.inbox.nothingToReview", "Nothing to review")}
            body={anyFilter ? t("approvals.inbox.tryWideningOrClearingAFilterToSeeMore", "Try widening or clearing a filter to see more.") : t("approvals.inbox.whenSomeoneSubmitsContentForApprovalItShowsUpHere", "When someone submits content for approval, it shows up here.")}
            action={anyFilter ? <Btn variant="secondary" onClick={() => { setStatusFilter("all"); setTypeFilter("all"); setProcFilter(null); setQuery(""); }}>{t("approvals.inbox.clearFilters", "Clear filters")}</Btn> : null} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 16 }}>
            {filtered.map((r) => (
              <Card key={`${r.type}:${r.itemId}`} onClick={() => nav.go("approvals.review", { type: r.type, id: r.itemId })}
                style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ height: 8, background: r.hue }} />
                <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, lineHeight: 1.25 }}>{r.title}</div>
                    <ApprovalStatusPill status={r.status} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>{typeBadge(r.tm)}<span style={{ fontSize: 12.5, color: T.soft }}>{r.process}</span></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                    <InitialsDot initials={(r.submittedBy || "?").split(" ").map((w) => w[0]).join("")} color={r.hue} />
                    <span style={{ fontSize: 12.5, color: T.n2 }}>{r.submittedBy} · {r.submittedAt}</span>
                  </div>
                </div>
                <CardActionBar left={r.feedback && r.feedback.length ? <span>{r.feedback.length} {t("approvals.inbox.note", "note")}{" "}{r.feedback.length === 1 ? "" : t("approvals.inbox.text", "s")}</span> : <span>{t("approvals.inbox.submitted", "Submitted")}{" "}{r.submittedAt}</span>} {...subActions(r)} />
              </Card>
            ))}
          </div>
        ) : (
          <Card style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font, fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.n6, color: T.soft, fontSize: 11, textTransform: "uppercase", fontWeight: 600 }}>
                  <th style={{ ...thStyle, width: 36, paddingRight: 0 }}><Checkbox checked={sel.allChecked} indeterminate={sel.someChecked} onChange={sel.toggleAll} /></th>
                  <th style={thStyle}>{t("approvals.inbox.item", "Item")}</th>
                  <th style={thStyle}>{t("approvals.inbox.type", "Type")}</th>
                  <th style={thStyle}>{t("approvals.inbox.status", "Status")}</th>
                  <th style={thStyle}>{t("approvals.inbox.process", "Process")}</th>
                  <th style={thStyle}>{t("approvals.inbox.submittedBy", "Submitted by")}</th>
                  <th style={{ ...thStyle, width: 140, textAlign: "right" }}>{t("approvals.inbox.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={`${r.type}:${r.itemId}`} onClick={() => nav.go("approvals.review", { type: r.type, id: r.itemId })} style={{ borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer", background: sel.selected.has(r.itemId) ? T.p4 : T.white }}>
                    <td style={{ ...tdStyle, paddingRight: 0 }} onClick={(e) => { e.stopPropagation(); sel.toggle(r.itemId); }}><Checkbox checked={sel.selected.has(r.itemId)} /></td>
                    <td style={tdStyle}><span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: r.hue, flexShrink: 0 }} /><span style={{ color: T.n1, fontWeight: 600 }}>{r.title}</span></span></td>
                    <td style={tdStyle}>{typeBadge(r.tm)}</td>
                    <td style={tdStyle}><ApprovalStatusPill status={r.status} /></td>
                    <td style={{ ...tdStyle, color: T.n2 }}>{r.process}</td>
                    <td style={{ ...tdStyle, color: T.soft }}>{r.submittedBy} · {r.submittedAt}</td>
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}><RowActions {...subActions(r)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
        <ExplainerFooter>
          <b>{t("approvals.inbox.approvals", "Approvals")}</b> {t("approvals.inbox.isTheReviewQueueForEverythingSubmittedForSignOffEventsCampaigns", "is the review queue for everything submitted for sign-off — events, campaigns, surveys, questions, groups and promos. Each submission routes to the approvers on its process. Manage who approves and which types require it in")}<span style={{ color: T.p1, fontWeight: 600, cursor: "pointer" }} onClick={() => nav.go("settings.approvals")}>{t("approvals.inbox.settingsApprovals", "Settings → Approvals")}</span>.
        </ExplainerFooter>
      </div>
    </Screen>
  );
}

/* ═══ Approver review screen (any content type) ══════════════════════════ */
function _apSummaryFields(type: string, it: Record<string, any> | null): [string, any][] {
  if (!it) return [];
  switch (type) {
    case "events": return [["Title", it.title], ["Type", it.template || it.type || "—"], ["When", `${it.date || "TBD"}${it.time ? " · " + it.time : ""}`], ["Location", it.location || "—"], ["Host", it.host || "—"], ["Capacity", it.capacity ? `${it.capacity}` : "—"]];
    case "campaigns": return [["Name", it.name], ["Channel", it.type || "—"], ["Audience", it.audience || "—"], ["Status", it.status || "—"], ["Recipients", it.recipients != null ? `${it.recipients}` : "—"]];
    case "surveys": return [["Name", it.name], ["Questions", `${(it.items || []).length}`], ["Channel", (it.send && it.send.channel) || "—"], ["Status", it.status || "—"]];
    case "questions": return [["Question", it.label], ["Type", it.type], ["Required", it.required ? "Yes" : "No"], ["Options", `${(it.options || []).length}`], ["Status", it.status || "—"]];
    case "question-groups": return [["Name", it.name], ["Questions", `${(it.items || []).length}`], ["Description", it.description || "—"]];
    case "promos": return [["Name", it.name], ["Code", it.code || "— (auto)"], ["Value", `${it.value}${it.kind === "percent" ? "%" : ""}`], ["Scope", it.scope || "all"], ["Type", it.type || "—"]];
    case "services": return [["Name", it.name], ["Duration", `${it.duration}m`], ["Capacity", `${it.capacity}`], ["Methods", `${(it.methods || []).length}`], ["Status", it.status || "—"]];
    default: return Object.entries(it).filter(([, v]) => typeof v !== "object").slice(0, 6) as [string, any][];
  }
}
export function ApproverReviewScreen() {
  return APPROVALS_COMING_SOON ? <ApprovalsQueueComingSoon /> : <ApproverReviewScreenLive />;
}
function ApproverReviewScreenLive() {
  const t = useT();
  const nav = useNav();
  const store = useApprovals();
  const type = (nav.params && (nav.params.type as string)) || "events";
  const itemId = (nav.params && ((nav.params.id || nav.params.eventId) as string)) || "";
  const tm = APPROVAL_TYPE(type);
  const item = tm.getItem(itemId);
  const sub = store.submission(type, itemId);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<{ type: string; name: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const pendingKindRef = useRef<"image" | "video">("image");
  useEffect(() => { if (sub && sub.status === "submitted") store.setReviewing(type, itemId); }, [type, itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sub) {
    return <Screen rail="approvals"><PageHeader title={t("approvals.inbox.review", "Review")} breadcrumb={<span style={{ cursor: "pointer" }} onClick={() => nav.go("approvals")}>{t("approvals.inbox.approvalsd6081ca1", "← Approvals")}</span>} /><div style={{ padding: 24 }}><EmptyState variant="card" icon={<Icon.warn size={22} />} title={t("approvals.inbox.nothingToReview", "Nothing to review")} body={t("approvals.inbox.thisItemHasnTBeenSubmittedForApproval", "This item hasn't been submitted for approval.")} /></div></Screen>;
  }
  const title = (item && tm.title(item)) || itemId;
  const fields = _apSummaryFields(type, item);
  const proc = store.getProcess(sub.processId);
  const decided = sub.status === "approved" || sub.status === "rejected";
  const mayDecide = canApproveType(type);
  // Addendum item 12: attachment references are server-minted, never invented.
  // A real file is picked, approvalsCreateAttachmentUpload registers it, the
  // bytes go to the returned signed target, and only the returned attachment
  // record is submitted. No file, no attachment.
  const attach = (kind: string) => { pendingKindRef.current = kind === "image" ? "image" : "video"; fileRef.current?.click(); };
  const onFilePicked = async (file: File | undefined) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const res = await approvalsCreateAttachmentUpload({ type: type as Parameters<typeof approvalsCreateAttachmentUpload>[0]["type"], itemId, fileName: file.name, mimeType: file.type || "application/octet-stream", kind: pendingKindRef.current });
      // Storage PUT to the signed URL the typed op minted — not an API mutation
      // (the mutation was approvalsCreateAttachmentUpload above). Routed
      // through the lib/api helper, never a raw fetch inline in the screen
      // (docket 2026-07-27 item 2 — the intake gate's raw-fetch scan flags
      // any bare `fetch(` in a screen component even when, as here, it's the
      // standard justified pattern for a pre-signed storage upload).
      await uploadToSignedUrl(res.uploadUrl, file);
      const att = res.attachment as unknown as { kind?: string; fileName?: string; name?: string };
      setAttachments((a) => [...a, { type: att.kind || pendingKindRef.current, name: att.fileName || att.name || file.name }]);
    } catch (e) {
      toast({ tone: "danger", title: t("approvals.inbox.attachmentDidnTUpload", "Attachment didn't upload"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const commit = (decision: string) => { store.addFeedback(type, itemId, { by: getUser().name || "Approver", text: text.trim(), attachments, decision }); setText(""); setAttachments([]); };
  const IconMap = Icon as unknown as Record<string, (p?: { size?: number }) => React.ReactNode>;
  const TI = IconMap[tm.icon] || IconMap.check;

  return (
    <Screen rail="approvals">
      <PageHeader
        title={i18nT("approvals.inbox.reviewTitle.icu", "Review · {value0}", { value0: title })}
        subtitle={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><ApprovalStatusPill status={sub.status} /><span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: T.soft }}>{TI({ size: 12 })} {tm.label}</span>{proc ? t("approvals.inbox.text5", "· {name}", { name: proc.name }) : ""}</span>}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("approvals")}><Icon.arrow dir="left" size={11} /> {t("approvals.inbox.approvals", "Approvals")}</span>}
        actions={<Btn variant="secondary" icon={<Icon.external size={14} />} onClick={() => tm.open(nav, itemId)}>{t("common.actions.open", "Open")}{" "}{tm.noun}</Btn>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px", display: "grid", gridTemplateColumns: "minmax(360px,1fr) minmax(320px,400px)", gap: 16 }}>
        {/* LEFT — summary + detail */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, marginBottom: 10 }}>{tm.label} {t("approvals.inbox.summary", "summary")}</div>
            {fields.length ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {fields.map(([k, v]) => (
                  <div key={k}><div style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.04 }}>{k}</div><div style={{ fontSize: 13, color: T.n1, marginTop: 2 }}>{String(v == null ? "—" : v)}</div></div>
                ))}
              </div>
            ) : <div style={{ fontSize: 13, color: T.soft }}>{t("approvals.inbox.this", "This")}{" "}{tm.noun} {t("approvals.inbox.couldNotBeLoadedForPreview", "could not be loaded for preview.")}</div>}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: "flex", gap: 8 }}>
              <Btn variant="secondary" icon={<Icon.external size={14} />} onClick={() => tm.open(nav, itemId)}>{t("approvals.inbox.seeFullDetail", "See full detail")}</Btn>
            </div>
          </Card>

          <Card style={{ padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{t("approvals.inbox.reviewStatus", "Review status")}</div>
            <ApprovalTimeline status={sub.status} />
          </Card>

          {sub.feedback && sub.feedback.length ? (
            <Card style={{ padding: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, marginBottom: 10 }}>{t("approvals.inbox.feedbackHistory", "Feedback history")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {sub.feedback.map((f) => (
                  <div key={f.id} style={{ padding: 12, background: T.n6, borderRadius: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{f.by}</span>
                      {f.decision ? <ApprovalStatusPill status={f.decision === "approved" ? "approved" : f.decision === "rejected" ? "rejected" : "feedback"} /> : null}
                      <span style={{ marginLeft: "auto", fontSize: 11, color: T.soft }}>{f.at}</span>
                    </div>
                    {f.text ? <div style={{ fontSize: 13, color: T.n2, lineHeight: 1.5 }}>{f.text}</div> : null}
                    {f.attachments && f.attachments.length ? <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>{f.attachments.map((at, i) => <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.p1, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 6, padding: "2px 8px" }}><Icon.eye size={11} />{at.name}</span>)}</div> : null}
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </div>

        {/* RIGHT — give feedback / decide */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("approvals.inbox.yourReview", "Your review")}</div>
            {decided ? (
              <div style={{ padding: 12, background: sub.status === "approved" ? T.green2 : T.red2, borderRadius: 8, fontSize: 13, color: T.n1 }}>
                {t("approvals.inbox.this", "This")}{" "}{tm.noun} {t("approvals.inbox.hasBeen", "has been")}{" "}<b>{sub.status}</b>{t("approvals.inbox.theSubmitterHasBeenNotified", ". The submitter has been notified.")}</div>
            ) : !mayDecide ? (
              <div style={{ padding: 12, background: T.n6, borderRadius: 8, fontSize: 13, color: T.n2, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: T.soft, flexShrink: 0, marginTop: 1 }}><Icon.eye size={15} /></span>
                <span>{t("approvals.inbox.youCanViewThis", "You can view this")}{" "}{tm.noun} {t("approvals.inbox.butDonTHavePermissionToApproveOrRejectItOnlyA", "but don't have permission to approve or reject it. Only a designated approver for this")}{" "}{tm.label.toLowerCase()} {t("approvals.inbox.processCanDecide", "process can decide.")}</span>
              </div>
            ) : (<>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder={t("approvals.inbox.leaveFeedbackForTheSubmitter", "Leave feedback for the submitter…")} style={apInput({ height: "auto", padding: 12, resize: "vertical", lineHeight: 1.5 })} />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn variant="secondary" size="sm" icon={<Icon.upload size={13} />} disabled={uploading} onClick={() => attach("image")}>{uploading ? t("approvals.inbox.uploading", "Uploading…") : t("approvals.inbox.attachScreenshot", "Attach screenshot")}</Btn>
                <Btn variant="secondary" size="sm" icon={<Icon.upload size={13} />} disabled={uploading} onClick={() => attach("video")}>{t("approvals.inbox.attachScreenRecording", "Attach screen recording")}</Btn>
                <input ref={fileRef} type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={(e) => void onFilePicked(e.target.files?.[0])} />
              </div>
              {attachments.length ? <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{attachments.map((a, i) => <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: T.n2, background: T.n6, border: `1px solid ${T.border}`, borderRadius: 6, padding: "3px 8px" }}>{a.name}<span onClick={() => setAttachments((arr) => arr.filter((_, k) => k !== i))} style={{ cursor: "pointer", display: "inline-flex" }}><Icon.x size={10} /></span></span>)}</div> : null}
              <div style={{ display: "flex", gap: 8, paddingTop: 6, borderTop: `1px solid ${T.border}` }}>
                <Btn variant="secondary" disabled={!text.trim()} onClick={() => commit("feedback")}>{t("approvals.inbox.sendFeedback", "Send feedback")}</Btn>
                <span style={{ flex: 1 }} />
                <Btn variant="secondary" onClick={() => commit("rejected")} style={{ color: T.red1 }}>{t("approvals.inbox.reject", "Reject")}</Btn>
                <Btn icon={<Icon.check size={14} />} onClick={() => commit("approved")}>{t("approvals.inbox.approve", "Approve")}</Btn>
              </div>
            </>)}
          </Card>
          {proc ? (
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 12, color: T.soft, marginBottom: 8 }}>{proc.requireAll ? t("approvals.inbox.text3", "All approvers must approve") : t("approvals.inbox.text4", "Any approver can approve")}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {proc.approvers.map((a) => <div key={a.email} style={{ fontSize: 12.5, color: T.n2, display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 6, height: 6, borderRadius: 999, background: T.n4 }} /> {a.name}</div>)}
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </Screen>
  );
}
